/**
 * Colar imagem — a parte que o chat interno, o atendimento (dos dois lados) e
 * o feed compartilham.
 *
 * Pedido do Augusto (13/10/2026): "muitas vezes a pessoa copia a imagem de
 * algum outro aplicativo e quer colar no chat. Não quer salvar a imagem, quer
 * colar." Colar é a terceira porta para o MESMO caminho do 📎 e do arrastar:
 * este módulo só entrega `File[]`, e quem decide o destino (editor de
 * marcação, cartão de preparo, galeria do feed) continua sendo o compositor,
 * que é quem tem o estado dos anexos. É o mesmo molde do `useSolturaDeArquivos`.
 *
 * ## Por que o texto ganha
 *
 * Excel e Word mandam, junto com o texto, uma "foto" da seleção. Se a imagem
 * ganhasse, colar três células no chat viraria um anexo — e a pessoa queria o
 * texto. Slack e Teams decidem do mesmo jeito.
 *
 * ## Por que renomear
 *
 * O Chrome chama TODA imagem colada de `image.png`: três prints seguidos
 * chegariam a quem recebe como três `image.png`. O nome novo carrega a hora da
 * colagem e é ASCII sem espaço — espaço e `#` no nome quebram a assinatura da
 * URL do anexo (ver a nota de voz no `MessageComposer`).
 *
 * ## Por que só imagem
 *
 * O pedido é imagem. Arquivo de outro tipo continua pelo 📎 ou pelo arrastar.
 */
import { useCallback, useRef } from "react";

/**
 * A colagem é TEXTO? Quando é, a imagem que veio junto é só uma "foto" do
 * texto (Excel, Word) e o navegador cola o texto como sempre colou.
 *
 * Quem chega aqui: o print da tela (só imagem), o "Copiar imagem" do navegador
 * (um `<img>` em HTML mais a imagem, sem `text/plain`), o WhatsApp Desktop (só
 * imagem) e o Office (`text/plain` + `text/html` + imagem).
 */
export function colagemEhTexto(dados: DataTransfer): boolean {
  // Só o `text/plain` decide. O HTML não serve de sinal: o "Copiar imagem"
  // também o preenche (é o `<img>`), e o Office manda os dois juntos.
  // Espaço e quebra de linha sozinhos não são texto que a pessoa quis colar.
  return dados.getData("text/plain").trim() !== "";
}

const EXTENSAO_POR_TIPO: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/bmp": "bmp",
  "image/svg+xml": "svg",
};

const doisDigitos = (n: number) => String(n).padStart(2, "0");

/** `20261013-143205`, no relógio de quem cola. */
function carimbo(agora: Date): string {
  const dia = `${agora.getFullYear()}${doisDigitos(agora.getMonth() + 1)}${doisDigitos(agora.getDate())}`;
  const hora = `${doisDigitos(agora.getHours())}${doisDigitos(agora.getMinutes())}${doisDigitos(agora.getSeconds())}`;
  return `${dia}-${hora}`;
}

/** As imagens da colagem. Safari antigo só as expõe em `items`, com `files` vazio. */
function imagensDe(dados: DataTransfer): File[] {
  const deFiles = Array.from(dados.files || []);
  const brutos = deFiles.length
    ? deFiles
    : Array.from(dados.items || [])
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile())
        .filter((f): f is File => f !== null);
  return brutos.filter((f) => f.type.startsWith("image/"));
}

/**
 * O que da colagem vira anexo: as imagens, já renomeadas. Lista vazia quando a
 * colagem é texto ou não traz imagem — aí o navegador segue com a colagem dele.
 *
 * @param agora só para o teste fixar o relógio.
 */
export function arquivosDaColagem(dados: DataTransfer | null, agora = new Date()): File[] {
  if (!dados || colagemEhTexto(dados)) return [];
  const base = `colada-${carimbo(agora)}`;
  return imagensDe(dados).map((f, i) => {
    const extensao = EXTENSAO_POR_TIPO[f.type] ?? "png";
    const nome = `${base}${i === 0 ? "" : `-${i + 1}`}.${extensao}`;
    return new File([f], nome, { type: f.type, lastModified: f.lastModified });
  });
}

/**
 * Devolve o `onPaste` da caixa de texto.
 *
 * Só toma o gesto quando há imagem para entregar: aí impede o navegador de
 * colar (senão o HTML do "Copiar imagem" ou o nome do arquivo cairia na caixa).
 * Colagem de texto passa direto.
 *
 * @param aoColar recebe as imagens coladas. Não é chamado com lista vazia.
 * @param desabilitado quando true, a colagem de imagem é ignorada (sessão
 *        encerrada, gravando áudio, enviando).
 */
export function useColagemDeArquivos(
  aoColar: (arquivos: File[]) => void,
  desabilitado = false
): (e: React.ClipboardEvent<HTMLElement>) => void {
  // O callback mora num ref pelo mesmo motivo do `useSolturaDeArquivos`: o
  // chat de atendimento passa uma arrow nova a cada render, e exigir
  // `useCallback` de quem chama seria uma armadilha calada.
  const aoColarAtual = useRef(aoColar);
  aoColarAtual.current = aoColar;

  return useCallback(
    (e: React.ClipboardEvent<HTMLElement>) => {
      if (desabilitado) return;
      const arquivos = arquivosDaColagem(e.clipboardData);
      if (!arquivos.length) return;
      e.preventDefault();
      aoColarAtual.current(arquivos);
    },
    [desabilitado]
  );
}
